import {IProductShort} from 'modules/products/types';
import {IAnalyticsMeta} from 'core/types';
import {useContext} from 'react';
import {ProductSnippetAnalyticsContext} from 'modules/analytics/contexts/ProductSnippetAnalyticsContext';
import {IProductSnippetAnalytics} from 'modules/analytics/types/ProductSnippetAnalytics';
import {IProductSnippet} from 'shared/types/productSnippet.types';
import {BRAND, YEAR} from 'modules/product-card/constants/characteristics';
import {IProduct} from 'modules/product-card/models/ProductResponse';
import {useGtagEventLocation} from 'hooks/analytics/gtag/helpers/useGtagEventLocation';

type TUseAnalyticsParams = {
    product: IProductShort | IProduct;
    analyticsData?: IAnalyticsMeta;
    price: IProductSnippet['price'];
    isFineAndRare: boolean;
};

// eslint-disable-next-line complexity
export const useAnalytics = ({product, analyticsData, price, isFineAndRare}: TUseAnalyticsParams) => {
    const {getTrackingHandlers} = useContext(ProductSnippetAnalyticsContext);

    const {eventLocation} = useGtagEventLocation();

    // Признак и число винотек для клика/добавления в корзину — см. «Разметка. PLP. Сниппет»:
    // is_only_in_stores = canBuy === false && canReserve === true.
    const isOnlyInStores = !product.canBuy && product.canReserve;
    const storesWithStockCount = product.storesWithStock?.length || 0;

    const brandProperty = Array.isArray(product.properties)
        ? product.properties.find((property) => property.code === BRAND.KEY)
        : undefined;
    const brand =
        'object' === typeof brandProperty?.value && null !== brandProperty?.value
            ? String((brandProperty.value as any).translate ?? '')
            : String(brandProperty?.value ?? '');

    const yearProperty = product.properties.find((property) => property.code === YEAR.KEY);

    const category = product.sections.find((item) => item.isCanonical);

    const productToAnalytics: IProductSnippetAnalytics = {
        id: Number(product.id),
        dimension3: price.percent || null,
        dimension13: product.ratings.find((item) => 'REVIEWS' === item.code)?.value || null,
        dimension14: product.meta?.saleCodes?.join('|') || '(not set)',
        dimension25: product.canBuy || product.canReserve ? 1 : 0,
        dimension48: isFineAndRare ? 1 : 0,
        price: price.current,
        brand: brand || null,
        variant: (yearProperty?.value as number) ?? null,
        position: 'number' === typeof analyticsData?.positionIndex ? analyticsData.positionIndex + 1 : null,
        metric1: product.price.base.price || null,
        metric2: null,
        quantity: null,
        name: product.name,
        list: analyticsData?.listName || eventLocation || null,
        category: product.sections.find((item) => item.isCanonical)?.title || null,
    };

    const {
        onProductClick,
        onAddToCartClick,
        onAddToWishlistClick,
        onRemoveFromCartClick,
        onCatalogClick,
        onReviewsClick,
        onStockClick,
        onProductView,
        onRemoveFromWishlistClick,
    } = getTrackingHandlers({
        productAnalytics: productToAnalytics,
        productUrl: product.url,
        category: {name: category?.title, id: category?.id},
        storeEventContext: {isOnlyInStores, storesWithStockCount},
    });

    return {
        onProductClick: () => {
            onProductClick();
        },
        onAddToCartClick,
        onRemoveFromCartClick,
        onAddToWishlistClick,
        onCatalogClick,
        onReviewsClick,
        onStockClick,
        onProductView,
        onRemoveFromWishlistClick,
    };
};
